import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ExternalLink, Send, Loader2, Mail, Eye, MoreHorizontal, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { getAllPosts } from '@/lib/blog';
import type { BlogPost } from '@/lib/blog';
import { buildNewsletterPayload } from '@/lib/newsletterContent';

function formatCategoryName(slug: string): string {
  return slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function postToEmailPayload(post: BlogPost) {
  return buildNewsletterPayload(post);
}

export const BlogManagement: React.FC = () => {
  const [sendingSlug, setSendingSlug] = useState<string | null>(null);
  const [confirmSendPost, setConfirmSendPost] = useState<BlogPost | null>(null);
  const [previewPost, setPreviewPost] = useState<BlogPost | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [testEmailPost, setTestEmailPost] = useState<BlogPost | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [newsletterFilter, setNewsletterFilter] = useState<'all' | 'sent' | 'not_sent'>('all');

  // Newsletter sent timestamps from Supabase
  const [newsletterLog, setNewsletterLog] = useState<Record<string, string>>({});
  const [logLoading, setLogLoading] = useState(true);

  const { toast } = useToast();
  const { user } = useAuth();
  const allPosts = getAllPosts();

  // Sync markdown posts to blog_posts table and fetch newsletter status
  useEffect(() => {
    const syncAndFetch = async () => {
      try {
        // Ensure every markdown post has a row in blog_posts
        const postsToSync = allPosts.map((p) => ({
          slug: p.slug,
          title: p.title,
          excerpt: p.excerpt || '',
          published: true,
          published_at: p.published_at,
        }));

        if (postsToSync.length > 0) {
          const { error: syncError } = await supabase
            .from('blog_posts')
            .upsert(postsToSync, { onConflict: 'slug', ignoreDuplicates: true });

          if (syncError) {
            console.error('Error syncing posts to DB:', syncError);
          }
        }

        // Fetch newsletter_sent_at for all posts
        const { data, error } = await supabase
          .from('blog_posts')
          .select('slug, newsletter_sent_at')
          .not('newsletter_sent_at', 'is', null);

        if (error) {
          console.error('Error fetching newsletter status:', error);
        } else if (data) {
          const log: Record<string, string> = {};
          for (const row of data) {
            if (row.slug && row.newsletter_sent_at) {
              log[row.slug] = row.newsletter_sent_at;
            }
          }
          setNewsletterLog(log);
        }
      } catch (error) {
        console.error('Error in syncAndFetch:', error);
      } finally {
        setLogLoading(false);
      }
    };

    syncAndFetch();
  }, []);

  const filteredPosts = useMemo(() => {
    if (newsletterFilter === 'all') return allPosts;
    if (newsletterFilter === 'sent') return allPosts.filter((p) => !!newsletterLog[p.slug]);
    return allPosts.filter((p) => !newsletterLog[p.slug]);
  }, [allPosts, newsletterLog, newsletterFilter]);

  const sentCount = allPosts.filter((p) => !!newsletterLog[p.slug]).length;
  const notSentCount = allPosts.length - sentCount;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleSendToNewsletter = async (post: BlogPost) => {
    setSendingSlug(post.slug);
    try {
      const payload = postToEmailPayload(post);
      const { data, error } = await supabase.functions.invoke('send-blog-newsletter', {
        body: payload,
      });
      if (error) throw error;

      // Update local state
      setNewsletterLog((prev) => ({ ...prev, [post.slug]: new Date().toISOString() }));

      toast({
        title: "Draft created in MailerLite",
        description: `"${post.title}" is ready to review and send.`,
      });
    } catch (error) {
      console.error('Newsletter send error:', error);
      toast({ title: "Error", description: "Failed to send newsletter.", variant: "destructive" });
    } finally {
      setSendingSlug(null);
      setConfirmSendPost(null);
    }
  };

  const handlePreview = (post: BlogPost) => {
    const payload = postToEmailPayload(post);
    setPreviewPost(post);
    setPreviewHtml(payload.email_html);
    setIsLoadingPreview(false);
  };

  const handleSendTestEmail = async () => {
    if (!testEmailPost || !testEmail) return;
    setIsSendingTest(true);
    try {
      const payload = postToEmailPayload(testEmailPost);
      const { data, error } = await supabase.functions.invoke('test-blog-newsletter', {
        body: { ...payload, testEmail },
      });
      if (error) throw error;
      toast({ title: "Test email sent!", description: `Check your inbox at ${testEmail}` });
      setTestEmailPost(null);
      setTestEmail('');
    } catch (error) {
      console.error('Test email error:', error);
      toast({ title: "Error", description: "Failed to send test email.", variant: "destructive" });
    } finally {
      setIsSendingTest(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header with filter */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {allPosts.length} posts published
        </p>
        <div className="flex gap-1">
          {(['all', 'sent', 'not_sent'] as const).map((filter) => {
            const label = filter === 'all' ? `All (${allPosts.length})` : filter === 'sent' ? `Sent (${sentCount})` : `Not sent (${notSentCount})`;
            return (
              <button
                key={filter}
                onClick={() => setNewsletterFilter(filter)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  newsletterFilter === filter
                    ? 'bg-foreground text-background'
                    : 'bg-secondary text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Post list */}
      <div className="border rounded-lg divide-y">
        {filteredPosts.length > 0 ? (
          filteredPosts.map((post) => {
            const sentAt = newsletterLog[post.slug];
            return (
              <div key={post.slug} className="flex items-center gap-4 px-4 py-3 hover:bg-muted/50 transition-colors">
                {/* Thumbnail */}
                {post.featured_image ? (
                  <img
                    src={post.featured_image}
                    alt=""
                    className="w-12 h-12 rounded object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded bg-muted flex-shrink-0" />
                )}

                {/* Post info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/blog/${post.slug}`}
                      className="font-medium text-foreground hover:text-primary transition-colors truncate"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {post.title}
                    </Link>
                    <ExternalLink className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                    <span>{formatDate(post.published_at)}</span>
                    {post.category && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                        {formatCategoryName(post.category)}
                      </Badge>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.reading_time} min
                    </span>
                  </div>
                </div>

                {/* Newsletter status */}
                <div className="flex-shrink-0 hidden sm:block w-32 text-right">
                  {logLoading ? (
                    <span className="text-xs text-muted-foreground">...</span>
                  ) : sentAt ? (
                    <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30 text-xs">
                      <Send className="w-3 h-3 mr-1" />
                      {formatDate(sentAt)}
                    </Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">Not sent</span>
                  )}
                </div>

                {/* Actions dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 flex-shrink-0">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handlePreview(post)}>
                      <Eye className="w-4 h-4 mr-2" />
                      Preview newsletter
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { setTestEmailPost(post); setTestEmail(user?.email || ''); }}>
                      <Mail className="w-4 h-4 mr-2" />
                      Send test email
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setConfirmSendPost(post)}
                      disabled={sendingSlug === post.slug}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Create draft in MailerLite
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <p>
              {newsletterFilter !== 'all'
                ? 'No posts match this filter.'
                : 'No posts yet. Add markdown files to content/blog/ to get started.'}
            </p>
          </div>
        )}
      </div>

      {/* Create Draft Confirmation */}
      <AlertDialog open={!!confirmSendPost} onOpenChange={() => setConfirmSendPost(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Create Newsletter Draft?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a draft campaign for "{confirmSendPost?.title}" in MailerLite. You can review and send it from there.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmSendPost && handleSendToNewsletter(confirmSendPost)}>
              <Send className="w-4 h-4 mr-2" />
              Create Draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Newsletter Preview Modal */}
      <Dialog open={!!previewPost} onOpenChange={() => { setPreviewPost(null); setPreviewHtml(null); }}>
        <DialogContent className="max-w-4xl h-[80vh]">
          <DialogHeader>
            <DialogTitle>Newsletter Preview</DialogTitle>
            <DialogDescription>{previewPost?.title}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-hidden rounded-md border bg-white">
            {isLoadingPreview ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : previewHtml ? (
              <iframe srcDoc={previewHtml} className="w-full h-full border-0" title="Newsletter Preview" sandbox="" />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={!!testEmailPost} onOpenChange={() => { setTestEmailPost(null); setTestEmail(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Send a test of "{testEmailPost?.title}" to check how it looks.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="test-email">Email Address</Label>
              <Input
                id="test-email"
                type="email"
                placeholder="your@email.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTestEmailPost(null); setTestEmail(''); }}>
              Cancel
            </Button>
            <Button onClick={handleSendTestEmail} disabled={!testEmail || isSendingTest}>
              {isSendingTest ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Send Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
